/* Positionen und ihre Zuordnung zu Mannschaftsteilen.
 *
 * Die Positionsbezeichnungen bleiben englisch — sie stehen so in
 * Verbandsformularen und Spielerpässen (siehe Entscheidung in CLAUDE.md,
 * Abschnitt Sprache). Übersetzt werden nur die vier Gruppenüberschriften,
 * die liegen unter positionGroup.<key> in den Sprachdateien.
 *
 * Gruppen (Auftrag C, 2026-10): Striker, Winger, Midfielder, Defense, Goalkeeper. Die frühere Gruppe "Offensive"
 * ('off') ist aufgeteilt: Winger = nur Left Wing und Right Wing, Striker = der Rest des Angriffs. Zugeordnet wird
 * ausschließlich nach der ERSTEN Position (p.pos); die zweite (p.pos2) spielt für die Gruppe keine Rolle.
 */

export const POSITIONS = [
  'Goalkeeper',
  'Center Back',
  'Left Back',
  'Right Back',
  'Right Wing Back',
  'Left Wing Back',
  'Defensive Midfielder',
  'Central Midfielder',
  'Offensive Midfielder',
  'Left Wing',
  'Right Wing',
  'Second Striker',
  'Central Striker',
];

export const FEET = ['Right', 'Left', 'Both'];

/* Platzhalter, solange keine Position gewählt ist (Vorgabe in ensurePlayerShape). Angezeigt wird er
   übersetzt (player.positionTbd); gespeichert und im Export erkannt wird dieser englische Wert. */
export const POSITION_TBD = 'Position TBD';

/* Reihenfolge in der Kaderliste: Angriff zuerst, Tor zuletzt. Entscheidung
   Bastian, 2026-09-17 (TODO.md, "Kaderliste lesbarer machen"); Angriff geteilt in Striker und Winger
   (Auftrag C, 2026-10). */
export const POSITION_GROUPS = ['st', 'wing', 'mid', 'def', 'gk', 'open'];

/* Gruppen, die einen Filterknopf und einen Eintrag in der Positionsauswahl haben. 'open' ("Position offen":
   Freitext, "Position TBD", leer) steht nur am Ende der Liste und im Export, hat keinen Chip und keine
   Auswahlgruppe; solche Spieler zählen bei "All". */
export const FILTER_GROUPS = POSITION_GROUPS.filter((key) => key !== 'open');

/* Reihenfolge innerhalb der Gruppe Defense (Ansicht "Position", Kaderliste, Transferred-Seite und Export):
   Left Back, Center Back, Right Back, dann Left und Right Wing Back. Andere Abwehr-Positionen (gibt es in
   POSITIONS keine; Freitext mit "back") stehen danach. Innerhalb einer Position gilt die bisherige Sortierung
   (Nachname bzw. Transferdatum). */
export const DEFENSE_ORDER = ['Left Back', 'Center Back', 'Right Back', 'Left Wing Back', 'Right Wing Back'];

/** Rang einer Abwehrposition in DEFENSE_ORDER (0 = links hinten); unbekannte stehen dahinter. Tolerant bei Freitext. */
export function defenseRank(pos) {
  const s = (pos || '').toLowerCase().replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();
  const exact = DEFENSE_ORDER.findIndex((d) => d.toLowerCase() === s);
  if (exact >= 0) return exact;
  const wing = s.includes('wing') && s.includes('back');
  if (wing && s.includes('left')) return 3;
  if (wing && s.includes('right')) return 4;
  if (s.includes('left') && s.includes('back')) return 0;
  if ((s.includes('center') || s.includes('centre') || s.includes('central')) && s.includes('back')) return 1;
  if (s.includes('right') && s.includes('back')) return 2;
  return DEFENSE_ORDER.length;
}

/* Kürzel für die kompakte Anzeige in der Kaderliste. Deckt alle Einträge aus
   POSITIONS ab. Gebräuchliche Fußball-Abkürzungen, keine reinen Initialen —
   deshalb z. B. "ST" für "Central Striker", nicht "CS". */
export const POSITION_ABBR = {
  Goalkeeper: 'GK',
  'Center Back': 'CB',
  'Left Back': 'LB',
  'Right Back': 'RB',
  'Right Wing Back': 'RWB',
  'Left Wing Back': 'LWB',
  'Defensive Midfielder': 'DM',
  'Central Midfielder': 'CM',
  'Offensive Midfielder': 'AM',
  'Left Wing': 'LW',
  'Right Wing': 'RW',
  'Second Striker': 'SS',
  'Central Striker': 'ST',
};

/**
 * Kürzel zu einer Position. `pos` kommt teils aus einem Freitextfeld und
 * steht dann nicht in POSITION_ABBR — Rückfall auf die Langform, lieber die
 * ausgeschriebene Position zeigen als ein leeres Feld.
 */
export function positionAbbr(pos) {
  if (!pos) return '';
  return POSITION_ABBR[pos] || pos;
}

/* Zuordnung über Substring-Vergleich — die Bezeichnungen kommen teils aus
   Freitextfeldern, deshalb bewusst tolerant. Reihenfolge ist relevant:
   "Left Wing Back" enthält sowohl "back" als auch "wing" und muss zur Abwehr,
   deshalb wird auf "back" vor allem anderen geprüft; "Left Midfielder" bleibt im
   Mittelfeld (die Prüfung auf "midfield" steht vor "wing").
   Winger sind nur Left Wing / Right Wing (auch "Left Winger", "LW", "RW"). Striker sind
   Central/Second Striker (auch "Striker", "Forward", "ST", "CF", "SS"). Alles, was
   keiner Gruppe zugeordnet werden kann (Freitext, "Position TBD", leer), ist 'open'
   ("Position offen"): eigener Abschnitt am Ende der Liste, ohne Chip, zählt bei "All". */
export function positionGroup(pos) {
  const s = (pos || '').toLowerCase().trim();
  if (s.includes('goalkeeper') || s === 'gk') return 'gk';
  if (s.includes('back')) return 'def';
  if (s.includes('midfield')) return 'mid';
  if (s.includes('wing') || s === 'lw' || s === 'rw') return 'wing';
  if (s.includes('striker') || s.includes('forward') || ['st', 'cf', 'ss'].includes(s)) return 'st';
  return 'open';
}

/**
 * POSITIONS nach Mannschaftsteil gruppiert, in der Reihenfolge von POSITION_GROUPS (Striker, Winger,
 * Mittelfeld, Abwehr, Tor) und innerhalb der Gruppe in der Reihenfolge von POSITIONS. Für gruppierte Auswahlfelder
 * (<optgroup>); die Überschrift steht unter positionGroup.<key> in den Sprachdateien.
 */
export function positionsByGroup() {
  return FILTER_GROUPS.map((key) => ({
    key,
    positions: POSITIONS.filter((pos) => positionGroup(pos) === key),
  }));
}

function byLastName(a, b) {
  return (a.lastName || '').toLowerCase().localeCompare((b.lastName || '').toLowerCase());
}

/* Innerhalb der Gruppe: in Defense erst nach Einzelposition (DEFENSE_ORDER), sonst/danach nach Nachname */
function byDefenseThenName(a, b) {
  return defenseRank(a.pos) - defenseRank(b.pos) || byLastName(a, b);
}

/**
 * Spieler nach Mannschaftsteil gruppieren, in der Reihenfolge von
 * POSITION_GROUPS. Innerhalb einer Gruppe nach Nachname sortiert.
 * Entscheidung Bastian, 2026-09-17: Vorher wurde nach dem vollen Namen
 * (also faktisch nach Vorname) sortiert — die Hauptursache dafür, dass ein
 * gesuchter Spieler in der Liste schwer zu finden war.
 */
export function groupByPosition(players) {
  return POSITION_GROUPS.map((key) => ({
    key,
    members: players.filter((p) => positionGroup(p.pos) === key).sort(key === 'def' ? byDefenseThenName : byLastName),
  })).filter((g) => g.members.length > 0);
}

/**
 * Spieler alphabetisch nach Nachname gruppiert, ein Eintrag je
 * Anfangsbuchstabe. Für den Sortierschalter "A–Z" in der Kaderliste —
 * dieselbe Form wie groupByPosition (key + members), damit SquadList.vue
 * beide Modi mit derselben Vorlage darstellen kann. Ohne Nachname landet ein
 * Spieler unter "#", nicht in einer zufälligen Gruppe.
 */
export function groupByLetter(players) {
  const sorted = players.slice().sort(byLastName);
  const groups = [];
  for (const p of sorted) {
    const letter = (p.lastName || '').trim().charAt(0).toUpperCase() || '#';
    const current = groups[groups.length - 1];
    if (current && current.key === letter) current.members.push(p);
    else groups.push({ key: letter, members: [p] });
  }
  return groups;
}
